import { DragEndEvent, DndContext, useDroppable, useDraggable } from '@dnd-kit/core';
import * as React from 'react';
import { agendaItemHelper, AgendaItem } from '../../../amorg-common';
import { moveAgendaItemToIndex } from '../../../api/activityClientApi';
import { Button } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { MainRightTwoColumnLayout } from '../../_layouts/TwoColumnLayouts';
import { ActivityComponentProps } from './ActivityComponentProps';
import { AgendaItemPanel, NoAgendaItemsPanelMessage } from './AgendaItemPanel';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { CardButton } from '../../../designSystem/CardButton';
import { DragButton } from '../../../designSystem/dragndrop/DragButton';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { ActionPointCounterCompositeIcon } from '../../../designSystem/compositeComponents/compositeIcons/ActionPointCounterCompositeIcon';
import { MeetingNotesCompositeIcon } from '../../../designSystem/compositeComponents/compositeIcons/MeetingNotesCompositeIcon';
import { Span, Span14 } from '../../../designSystem/typography/Body';
import { colors } from '../../../designSystem/colors/color';
import { Icon } from '../../../designSystem/icons/Icons';

export function AgendaItemMenuView (props: ActivityComponentProps & { onClickCreateAgendaItem: () => void }): JSX.Element
{
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() =>
    {
        if (props.activity.agendaItems.length > 0 && selectedId === null)
        {
            setSelectedId(agendaItemHelper.getSortedAgendaItems(props.activity.agendaItems)[0].id);
        }
    }, [props.activity]);


    async function handleClickCreateAgendaItem ()
    {
        props.onClickCreateAgendaItem();
    }

    const sorted = agendaItemHelper.getSortedAgendaItems(props.activity.agendaItems);
    const selectedIndex = sorted.findIndex(ai => ai.id === selectedId);
    const selectedAgendaItem = selectedIndex === -1 ? null : sorted[selectedIndex];

    async function handleDragEnd (event: DragEndEvent)
    {
        const sourceId = event.active.id.toString().substring(7);
        const targetId = event.over?.id.toString().substring(7);

        if (!targetId || sourceId === targetId)
            return;

        const rootAgendaItems = sorted.filter(ai => ai.parentId === '');
        const sourceIndex = rootAgendaItems.findIndex(ai => ai.id === sourceId);
        const targetIndex = rootAgendaItems.findIndex(ai => ai.id === targetId);
        if (sourceIndex === -1 || targetIndex === -1)
            return;

        setLoading(true);
        const result = await moveAgendaItemToIndex(sourceId, props.activity.id, targetIndex);
        if (result.isSuccess)
            props.onActivityUpdated();
        setLoading(false);
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            <MainRightTwoColumnLayout
                left={
                    <>
                        <ButtonRow variant="stretched" marginBottom={4}>
                            <Button variant="filled" icon="actions_plus" size="32px" text="Opret agendapunkt" onClick={handleClickCreateAgendaItem} />
                        </ButtonRow>

                        {props.activity.agendaItems.length === 0 &&
                            <div style={{  
                                backgroundColor:'white',
                                border:`1px dashed ${colors.card.outlined.default.border}`,
                                borderRadius:'4px',
                                padding: '32px'
                            }}>
                                <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} style={{ textAlign: "center" }}>
                                    <Icon name="list" color={colors.icon.active} size="medium" />
                                    <Span size="16px" fontWeight={500} color='normal'>Ingen agendapunkter</Span>
                                    <Span size="14px" fontWeight={450} color='gray'>Tilføj punkter til dagsordenen for at strukturere mødet og tildele handlepunkter</Span>
                                </Flex>

                            </div>
                        }

                        {props.activity.agendaItems.length > 0 && 
                            <Card variant="lightGray" style={{ minHeight: '120px' }}>

                                <DndContext onDragEnd={handleDragEnd}>
                                    <Flex flexDirection="column" gap={4}>
                            
                                        {sorted.map(item => (
                                            <DraggableAgendaMenuItem
                                                key={item.id}
                                                agendaItem={item}
                                                isDragEnabled={item.parentId === ''}
                                                onClick={() => setSelectedId(item.id)} />
                                        ))}
                                    </Flex>
                                </DndContext>
                            </Card>
                        }
                    </>
                }
                main={
                    <div>
                    
                        {selectedAgendaItem === null &&
                            <NoAgendaItemsPanelMessage onClickCreateAgendaItem={handleClickCreateAgendaItem} />
                        }

                        {selectedAgendaItem !== null &&
                            <AgendaItemPanel
                                activityId={props.activity.id}
                                agendaItem={selectedAgendaItem as AgendaItem}
                                agendaItemIndex={selectedIndex}
                                agendaItemsCount={props.activity.agendaItems.length}
                                users={props.users}
                                isDragEnabled={false}
                                hideCounter
                                hideStats
                                onUpdated={props.onActivityUpdated} />
                        }
                    </div>} 
                />
        </>

    );
}

type DraggableAgendaMenuItemProps = {
    agendaItem: AgendaItem,
    isDragEnabled: boolean,
    onClick: () => void;
};

function DraggableAgendaMenuItem (props: DraggableAgendaMenuItemProps): JSX.Element
{
    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
        id: `agenda_${props.agendaItem.id}`,
        disabled: !props.isDragEnabled,
    });

    const { attributes, listeners, setNodeRef: setDraggableNodeRef, setActivatorNodeRef, transform } = useDraggable({
        id: `agenda_${props.agendaItem.id}`,
        disabled: !props.isDragEnabled,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    return (
        <div
            ref={(node) =>
            {
                setDroppableNodeRef(node);
                setDraggableNodeRef(node);
            }}
            {...attributes}
            style={{ ...draggableStyle }}>
            <CardButton variant={isOver ? 'light' : 'outlined'} padding="4px" onClick={props.onClick}>
                <Flex alignItems="center" gap={8}>
                    {props.isDragEnabled &&
                        <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />
                    }
                    <Flex flexDirection="column" gap={8} margin="8px 8px 8px 0px">
                        <div style={{minWidth: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Span14 color="normal" fontWeight={500}>
                                {props.agendaItem.title}
                            </Span14>
                        </div>
                        <Flex gap={8}>
                            <ActionPointCounterCompositeIcon actionpoints={props.agendaItem.actionPoints} />
                            <MeetingNotesCompositeIcon notes={props.agendaItem.note} />
                        </Flex>
                    </Flex>
                </Flex>
            </CardButton>
        </div>
    );
}